import React, { useEffect, useMemo, useState } from 'react';
import './UserDetailModal.css';

const VISITS_PAGE_SIZE = 10;

function firstProductImage(product) {
  if (!product) return null;
  if (typeof product.image === 'string' && product.image) return product.image;
  const imgs = product.images;
  if (Array.isArray(imgs) && imgs.length) {
    const x = imgs[0];
    return typeof x === 'string' ? x : x?.url || null;
  }
  return product.thumbnail || product.coverImage || null;
}

function cartRawItems(raw) {
  if (!raw) return [];
  if (raw && typeof raw === 'object' && raw.success && raw.data !== undefined && raw.data !== raw) {
    return cartRawItems(raw.data);
  }
  const inner = raw.data !== undefined && typeof raw.data === 'object' ? raw.data : raw;
  if (Array.isArray(inner)) return inner;
  const nested = inner.cart ?? inner.snapshot ?? inner.payload;
  if (nested && typeof nested === 'object') {
    const fromNested = nested.items ?? nested.cartItems ?? nested.lines;
    if (Array.isArray(fromNested)) return fromNested;
  }
  const items = inner.items ?? inner.cartItems ?? inner.lines ?? inner.products;
  return Array.isArray(items) ? items : [];
}

function wishlistRawItems(raw) {
  if (!raw) return [];
  if (raw && typeof raw === 'object' && raw.success && raw.data !== undefined && raw.data !== raw) {
    return wishlistRawItems(raw.data);
  }
  const inner = raw.data !== undefined && typeof raw.data === 'object' ? raw.data : raw;
  if (Array.isArray(inner)) return inner;
  const items =
    inner.items ??
    inner.products ??
    inner.wishlist ??
    inner.likes ??
    inner.likedProducts ??
    inner.likedItems;
  return Array.isArray(items) ? items : [];
}

function visitsRawList(raw) {
  if (!raw) return [];
  if (raw && typeof raw === 'object' && raw.success && raw.data !== undefined && raw.data !== raw) {
    return visitsRawList(raw.data);
  }
  const inner = raw.data !== undefined && typeof raw.data === 'object' ? raw.data : raw;
  if (Array.isArray(inner)) return inner;
  const list =
    inner.visits ??
    inner.items ??
    inner.history ??
    inner.views ??
    inner.recentVisits ??
    inner.productViews;
  return Array.isArray(list) ? list : [];
}

function normalizeCartLines(user) {
  const raw =
    user?._adminShopExtras?.cart ??
    user?.cartSnapshot ??
    user?.cart ??
    user?.shoppingCart;
  return cartRawItems(raw).map((line, idx) => {
    const p =
      (line && typeof line.product === 'object' && line.product) ||
      (line && typeof line.productId === 'object' && line.productId) ||
      {};
    const name = p.name || line.name || line.title || 'Product';
    const id = p._id || p.id || line.productId || line.product || `row-${idx}`;
    const qty = Number(line.quantity ?? line.qty ?? 1) || 1;
    const price = line.price ?? line.unitPrice ?? p.price;
    return {
      key: `${String(id)}-${idx}`,
      name,
      qty,
      price: price != null ? Number(price) : null,
      variant: [line.color, line.size].filter(Boolean).join(' · ') || null,
      image: firstProductImage(p) || (typeof line.image === 'string' ? line.image : null),
    };
  });
}

function normalizeWishlistLines(user) {
  const raw =
    user?._adminShopExtras?.wishlist ??
    user?.wishlistSnapshot ??
    user?.wishlistItems ??
    user?.wishlist ??
    user?.likedProducts;
  return wishlistRawItems(raw).map((row, idx) => {
    const p =
      (row && typeof row.product === 'object' && row.product) ||
      (row && typeof row.productId === 'object' && row.productId) ||
      (row && (row.id || row._id) && row.name ? row : null) ||
      {};
    const name = p.name || row.name || row.title || 'Product';
    const id = p._id || p.id || row.productId || row.product || row.id || row._id || `w-${idx}`;
    return {
      key: `${String(id)}-${idx}`,
      name,
      addedAt: row.addedAt || row.createdAt || row.updatedAt,
      image: firstProductImage(p) || (typeof row.image === 'string' ? row.image : null),
    };
  });
}

function normalizeRecentVisits(user) {
  const raw =
    user?._adminShopExtras?.recentVisits ??
    user?.recentVisits ??
    user?.productViews ??
    user?.browsingHistory ??
    user?.recentlyViewedProducts;
  return visitsRawList(raw).map((row, idx) => {
      const p =
        (row && typeof row.product === 'object' && row.product) ||
        (row && typeof row.productId === 'object' && row.productId) ||
        {};
      const name = p.name || row.productName || row.name || row.title || 'Product';
      const id = p._id || p.id || row.productId || row.product || `v-${idx}`;
      const at =
        row.visitedAt ||
        row.lastVisitedAt ||
        row.viewedAt ||
        row.lastSeen ||
        row.timestamp ||
        row.createdAt;
      return {
        key: `${String(id)}-${idx}`,
        name,
        at,
        image: firstProductImage(p) || (typeof row.image === 'string' ? row.image : null),
      };
    });
}

const UserDetailModal = ({ user, isOpen, loading, onClose }) => {
  const cartLines = useMemo(() => normalizeCartLines(user), [user]);
  const wishlistLines = useMemo(() => normalizeWishlistLines(user), [user]);
  const visitLines = useMemo(() => normalizeRecentVisits(user), [user]);

  const [visitsExpanded, setVisitsExpanded] = useState(true);
  const [visitsPage, setVisitsPage] = useState(1);

  const userKey = user?._id || user?.id;

  useEffect(() => {
    setVisitsPage(1);
    setVisitsExpanded(true);
  }, [userKey]);

  const visitsTotalPages = Math.max(1, Math.ceil(visitLines.length / VISITS_PAGE_SIZE));
  const visitsPageSafe = Math.min(visitsPage, visitsTotalPages);

  useEffect(() => {
    if (visitsPage > visitsTotalPages) {
      setVisitsPage(visitsTotalPages);
    }
  }, [visitsPage, visitsTotalPages]);

  const visitLinesPage = useMemo(() => {
    const start = (visitsPageSafe - 1) * VISITS_PAGE_SIZE;
    return visitLines.slice(start, start + VISITS_PAGE_SIZE);
  }, [visitLines, visitsPageSafe]);

  if (!isOpen || !user) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Rs. 0';
    return `Rs. ${amount.toLocaleString()}`;
  };

  const getStatusColor = (status) => {
    const s = String(status || '').toLowerCase();
    const colors = {
      active: '#166534',
      inactive: '#78716c',
      banned: '#9f1239',
      pending: '#b45309',
      delivered: '#166534',
      completed: '#166534',
      shipped: '#0f766e',
      processing: '#a16207',
      cancelled: '#991b1b',
      refunded: '#6b21a8',
    };
    return colors[s] || '#78716c';
  };

  const getRoleColor = (role) => {
    const r = String(role || '').toLowerCase();
    const colors = {
      admin: '#9f1239',
      user: '#a16207',
      moderator: '#6d28d9',
      vendor: '#166534',
    };
    return colors[r] || '#78716c';
  };

  return (
    <div className="user-detail-modal-overlay" onClick={onClose}>
      <div className="user-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>User Details - {user.name}</h3>
          <button onClick={onClose} className="close-btn">
            ✕
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading user details...</p>
          </div>
        ) : (
          <div className="modal-body">
            {/* User Profile Section */}
            <div className="user-profile-section">
              <div className="profile-header">
                {/* Profile Avatar - Hidden */}
                {/* <div className="profile-avatar">
                  {user.avatar?.url ? (
                    <img src={user.avatar.url} alt={user.name} />
                  ) : (
                    <div className="avatar-placeholder">
                      <span>{user.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                    </div>
                  )}
                </div> */}
                <div className="profile-info">
                  <h4>{user.name}</h4>
                  <p className="profile-email">{user.email}</p>
                  <div className="profile-badges">
                    <span 
                      className="role-badge"
                      style={{ backgroundColor: getRoleColor(user.role) }}
                    >
                      {user.role || 'user'}
                    </span>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(user.isActive ? 'active' : 'inactive') }}
                    >
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                    {user.isDeleted && (
                      <span className="deleted-badge">Deleted</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* User Statistics */}
            <div className="user-stats-section">
              <h5>Account Statistics</h5>
              <div className="user-detail-account-stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">📅</div>
                  <div className="stat-content">
                    <div className="stat-label">Joined</div>
                    <div className="stat-value">{formatDate(user.createdAt)}</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">🔄</div>
                  <div className="stat-content">
                    <div className="stat-label">Last Updated</div>
                    <div className="stat-value">{formatDate(user.updatedAt)}</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">📱</div>
                  <div className="stat-content">
                    <div className="stat-label">Phone</div>
                    <div className="stat-value">{user.phone || 'N/A'}</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">🆔</div>
                  <div className="stat-content">
                    <div className="stat-label">User ID</div>
                    <div className="stat-value">{user._id}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent visits, bag, wishlist (profile + admin snapshots when available) */}
            <div className="admin-user-shop-activity">
              <section className="admin-user-visits-panel">
                <button
                  type="button"
                  className="admin-user-visits-toggle"
                  onClick={() => setVisitsExpanded((open) => !open)}
                  aria-expanded={visitsExpanded}
                  aria-controls="admin-user-recent-visits-panel"
                >
                  <span className="admin-user-visits-toggle-label">
                    Recent visits ({visitLines.length})
                  </span>
                  <span className="admin-user-visits-toggle-icon" aria-hidden>
                    {visitsExpanded ? '▼' : '▶'}
                  </span>
                </button>
                {visitsExpanded && (
                  <div id="admin-user-recent-visits-panel" className="admin-user-visits-body">
                    <p className="admin-user-shop-hint">
                      Products this user recently viewed (last 15 days), from profile or admin API.
                    </p>
                    <div className="admin-user-shop-list">
                {visitLines.length === 0 ? (
                  <p className="admin-user-shop-empty">No recent visits recorded.</p>
                ) : (
                  visitLinesPage.map((row) => (
                    <div key={row.key} className="admin-user-shop-row">
                      {row.image ? (
                        <img className="admin-user-shop-thumb" src={row.image} alt="" />
                      ) : (
                        <div className="admin-user-shop-thumb admin-user-shop-thumb--placeholder" aria-hidden />
                      )}
                      <div className="admin-user-shop-meta">
                        <div className="admin-user-shop-name">{row.name}</div>
                        {row.at ? (
                          <div className="admin-user-shop-sub">{formatDate(row.at)}</div>
                        ) : null}
                      </div>
                    </div>
                        ))
                      )}
                    </div>
                    {visitLines.length > VISITS_PAGE_SIZE && (
                      <div
                        className="admin-user-visits-pagination"
                        role="navigation"
                        aria-label="Recent visits pages"
                      >
                        <button
                          type="button"
                          className="admin-user-visits-pagination-btn"
                          disabled={visitsPageSafe <= 1}
                          onClick={() => setVisitsPage((p) => Math.max(1, p - 1))}
                        >
                          Previous
                        </button>
                        <span className="admin-user-visits-pagination-status">
                          Page {visitsPageSafe} of {visitsTotalPages}
                          <span className="admin-user-visits-pagination-count">
                            ({visitLines.length} products)
                          </span>
                        </span>
                        <button
                          type="button"
                          className="admin-user-visits-pagination-btn"
                          disabled={visitsPageSafe >= visitsTotalPages}
                          onClick={() =>
                            setVisitsPage((p) => Math.min(visitsTotalPages, p + 1))
                          }
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </section>

              <h5 className="admin-user-shop-heading">Shopping bag ({cartLines.length})</h5>
              <p className="admin-user-shop-hint">
                Current cart line items from the user profile or admin cart snapshot.
              </p>
              <div className="admin-user-shop-list">
                {cartLines.length === 0 ? (
                  <p className="admin-user-shop-empty">No cart items to show.</p>
                ) : (
                  cartLines.map((line) => (
                    <div key={line.key} className="admin-user-shop-row">
                      {line.image ? (
                        <img className="admin-user-shop-thumb" src={line.image} alt="" />
                      ) : (
                        <div className="admin-user-shop-thumb admin-user-shop-thumb--placeholder" aria-hidden />
                      )}
                      <div className="admin-user-shop-meta">
                        <div className="admin-user-shop-name">{line.name}</div>
                        {line.variant ? <div className="admin-user-shop-sub">{line.variant}</div> : null}
                        <div className="admin-user-shop-sub">
                          Qty {line.qty}
                          {line.price != null && !Number.isNaN(line.price) ? ` · ${formatCurrency(line.price)}` : ''}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <h5 className="admin-user-shop-heading">Wishlist / likes ({wishlistLines.length})</h5>
              {(user.wishlist?.itemCount != null || user.wishlist?.lastUpdated) && (
                <div className="admin-user-wishlist-summary">
                  {user.wishlist?.itemCount != null && (
                    <span>
                      Items (summary): <strong>{user.wishlist.itemCount}</strong>
                    </span>
                  )}
                  {user.wishlist?.lastUpdated && (
                    <span>
                      Last updated: <strong>{formatDate(user.wishlist.lastUpdated)}</strong>
                    </span>
                  )}
                </div>
              )}
              <p className="admin-user-shop-hint">
                Saved or liked products from the profile or admin wishlist snapshot.
              </p>
              <div className="admin-user-shop-list">
                {wishlistLines.length === 0 ? (
                  <p className="admin-user-shop-empty">No wishlist products loaded.</p>
                ) : (
                  wishlistLines.map((row) => (
                    <div key={row.key} className="admin-user-shop-row">
                      {row.image ? (
                        <img className="admin-user-shop-thumb" src={row.image} alt="" />
                      ) : (
                        <div className="admin-user-shop-thumb admin-user-shop-thumb--placeholder" aria-hidden />
                      )}
                      <div className="admin-user-shop-meta">
                        <div className="admin-user-shop-name">{row.name}</div>
                        {row.addedAt ? (
                          <div className="admin-user-shop-sub">{formatDate(row.addedAt)}</div>
                        ) : null}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Orders Section */}
            {user.orders && user.orders.length > 0 && (
              <div className="orders-section">
                <h5>Recent Orders ({user.orders.length})</h5>
                <div className="orders-list">
                  {user.orders.slice(0, 5).map((order, index) => (
                    <div key={order._id || index} className="order-item">
                      <div className="order-info">
                        <div className="order-number">
                          Order #{order.orderNumber || order._id?.slice(-8)}
                        </div>
                        <div className="order-date">{formatDate(order.createdAt)}</div>
                      </div>
                      <div className="order-total">{formatCurrency(order.total)}</div>
                      <div className="order-status">
                        <span 
                          className="status-badge"
                          style={{ backgroundColor: getStatusColor(order.status) }}
                        >
                          {order.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                {user.orders.length > 5 && (
                  <div className="more-orders">
                    <p>... and {user.orders.length - 5} more orders</p>
                  </div>
                )}
              </div>
            )}

            {/* Addresses Section */}
            {user.addresses && user.addresses.length > 0 ? (
              <div className="addresses-section">
                <h5>Shipping Addresses ({user.addresses.length})</h5>
                <div className="addresses-list">
                  {user.addresses.map((address, index) => (
                    <div key={address._id || index} className="address-item">
                      <div className="address-header">
                        <span className="address-type">
                          {address.type === 'home' ? '🏠 Home' : address.type === 'work' ? '💼 Work' : address.type === 'other' ? '📍 Other' : address.type || 'Home'}
                        </span>
                        {address.isDefault && (
                          <span className="default-badge">Default</span>
                        )}
                      </div>
                      <div className="address-details">
                        {address.addressLine1 && (
                          <div className="address-line"><strong>{address.addressLine1}</strong></div>
                        )}
                        {address.addressLine2 && (
                          <div className="address-line">{address.addressLine2}</div>
                        )}
                        <div className="address-line">
                          {address.city}{address.state && `, ${address.state}`}{address.postalCode && ` ${address.postalCode}`}
                        </div>
                        {address.country && (
                          <div className="address-line">{address.country}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="addresses-section">
                <h5>Shipping Addresses</h5>
                <div className="no-addresses">
                  <p>No shipping addresses found</p>
                </div>
              </div>
            )}

            {/* Additional Information */}
            <div className="additional-info-section">
              <h5>Additional Information</h5>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Email Verified:</span>
                  <span className={`info-value ${user.emailVerified ? 'verified' : 'unverified'}`}>
                    {user.emailVerified ? '✅ Yes' : '❌ No'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Account Status:</span>
                  <span className={`info-value ${user.isActive ? 'active' : 'inactive'}`}>
                    {user.isActive ? '✅ Active' : '❌ Inactive'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Account Type:</span>
                  <span className="info-value">{user.role || 'User'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Profile Complete:</span>
                  <span className="info-value">
                    {user.phone && user.addresses?.length > 0 ? '✅ Complete' : '⚠️ Incomplete'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button onClick={onClose} className="close-button">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDetailModal;

